import MESSAGES from '../../../../../../../../../appMessages';
import ADEException from '../ADEExceptionFlow/utils';
import DefaultException from '../DefaultExceptionFlow/utils';
import { isExceptionNumberRequired } from '../utils';

// Check if default exception has all required fields filled
const hasAnyDefaultInput = (defaults = []) =>
  (defaults || []).some(
    (ex) =>
      Boolean(ex?.code) &&
      Boolean(ex?.pieces) &&
      Boolean(ex?.packageType) &&
      Boolean(ex?.description)
  );

const normalizeExceptions = (exceptions = []) => {
  const list = Array.isArray(exceptions) ? exceptions : [];

  // BOTH flow structure: All exceptions have adeNumber embedded
  // Format: [{adeNumber: X, code: Y, pieces: 1, ...}, {adeNumber: X, code: Z, pieces: 2, ...}]
  // If empty, ensure at least one default exception exists (will get adeNumber when added)
  if (!list.length) {
    const defaultEx = DefaultException.getExceptionDetailsObject();
    return [{ ...defaultEx, adeNumber: null }];
  }

  // Ensure all exceptions have adeNumber field (even if null)
  // Extract adeNumber from first exception that has it, use for all
  const firstWithADE = list.find(ex => ex?.adeNumber != null);
  const commonAdeNumber = firstWithADE?.adeNumber || null;

  // Normalize: ensure all exceptions have adeNumber and are in correct format
  const normalized = list.map(ex => ({
    ...DefaultException.getExceptionDetailsObject(ex), // Ensure default fields structure
    adeNumber: ex?.adeNumber != null ? ex.adeNumber : commonAdeNumber // Use existing or common value
  }));

  return normalized;
};

const BothException = (config = {}) => {
  const { exceptionNumberLabel, exceptionNumberRequired } = config;
  const adeUtils = ADEException(config);
  // BOTH flow defaults to optional.
  const isAdeNumberRequired = isExceptionNumberRequired(exceptionNumberRequired, false);

  const validateExceptionDetail = (exceptionDetail = [], isReturn = false) => {
    const normalizedExceptions = normalizeExceptions(exceptionDetail);
    const firstException = normalizedExceptions[0] || {};
    const adeNumber = firstException?.adeNumber;
    const hasADE = Boolean(adeNumber);

    // Validate adeNumber for all exceptions (they all have the same value)
    let adeError = null;
    if (hasADE) {
      adeError = adeUtils.validateExceptionField('adeNumber', adeNumber);
    } else if (isAdeNumberRequired) {
      adeError = MESSAGES.PROCESS_DELIVERY.ADE_REQUIRED_DYNAMIC.replace('{label}', exceptionNumberLabel);
    }

    // Run per-field validation so only empty fields show "required" (not all fields when one is missing)
    const validatedDefault = DefaultException.validateExceptionDetail(normalizedExceptions, isReturn);

    const hasDefaultErrors = validatedDefault && validatedDefault.some(ex => {
      const errors = ex.errorInfo || {};
      return Object.values(errors).some(err => Boolean(err));
    });

    if (!adeError && !hasDefaultErrors) return null;

    // Apply adeNumber error to all exceptions if present
    const result = (validatedDefault || normalizedExceptions).map(ex => ({
      ...ex,
      errorInfo: {
        ...(ex.errorInfo || {}),
        ...(adeError ? { adeNumber: adeError } : {})
      }
    }));

    return result;
  };

  const getDefaultExceptionDetail = (exceptionDetail = []) => {
    const normalizedExceptions = normalizeExceptions(exceptionDetail);
    // Extract adeNumber from first exception (all should have same value)
    const commonAdeNumber = normalizedExceptions[0]?.adeNumber ?? null;
    
    // Return all exceptions with proper structure
    return normalizedExceptions.map((d) => ({
      ...DefaultException.getExceptionDetailsObject(d),
      adeNumber: d?.adeNumber ?? commonAdeNumber
    }));
  };

  const getExceptionRequestPayload = (exceptions = [], isReturn = false) => {
    const normalizedExceptions = normalizeExceptions(exceptions);

    const hasDefaultExceptionData = hasAnyDefaultInput(normalizedExceptions);
   // For delivery exception (DE) we require at least one complete row; for return (RTN) we still send adeNumber if entered
    if (!isReturn && !hasDefaultExceptionData) {
      return [];
    }

    // All exceptions already have adeNumber embedded, just need to format payload
    return normalizedExceptions.map(ex => {
      const { errorInfo, ...payload } = ex;
      if (isReturn) {
        const { code, ...rest } = payload;
        return rest;
      }
      return payload;
    });
  };

  const validateExceptionField = (name, value) => {
    if (name === 'adeNumber') {
      if (value == null || value === '') {
        return isAdeNumberRequired
          ? MESSAGES.PROCESS_DELIVERY.ADE_REQUIRED_DYNAMIC.replace('{label}', exceptionNumberLabel)
          : null;
      }
      return adeUtils.validateExceptionField('adeNumber', value);
    }
    if (['pieces', 'packageType', 'description', 'code'].includes(name)) {
      return DefaultException.validateExceptionField(name, value);
    }
    return null;
  };

  const getExceptionDetailsObject = () => DefaultException.getExceptionDetailsObject();
  

  return {
    validateExceptionField,
    validateExceptionDetail,
    getExceptionDetailsObject,
    getDefaultExceptionDetail,
    getExceptionRequestPayload,
  };
};

export default BothException;
